import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { LandingPageLayout } from "fe-web-components/layout/landingPageLayout";
import { Redirect, Route, Switch, useHistory } from "react-router";
import { ClinicWelcome } from "./clinicWelcome";
import { ClinicSignup } from "./clinicSignup";
import { ClinicLogin } from "./clinicLogin";
import { ClinicForgotPassword } from "./clinicForgotPassword";
import { ClinicRoute } from "../../../clinic/ClinicRoutes";
import { ClinicCreate } from "./clinicCreate";
import { ClinicVerifySignUp } from "./clinicVerifySignUp";
import { useQueryParams } from "fe-common/useQueryParams";
import { ClinicNoAccess } from "./clinicNoAccess";
import { ClinicStart } from "./clinicStart";
import { CLINIC_MANAGE_URL } from "../clinicSettings/clinicUrls";
import { PageTitleContext } from "../../../context/pageTitleContext";
import { isValidSession } from "user-management/src/cognito/kahunUserAuth";
import { DemoCase } from "./demoCase";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface OnboardRouterProps {
  invalidClinic?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const OnboardRouter = ({ ...props }: OnboardRouterProps) => {
  const history = useHistory();

  const pageTitle = useContext(PageTitleContext);

  const [demoMode, setDemoMode] = useState(false);
  const [validUser, setValidUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const valid = await isValidSession();
      setValidUser(valid);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    pageTitle.setPageTitle("Kahun Clinic");
  }, [pageTitle]);

  const goto = (path: string) => {
    history.push(`${CLINIC_MANAGE_URL}/${path}`);
  };

  const queryParams = useQueryParams();
  const clinicDisplayName = queryParams.get("clinic") || "";

  const gotoSignIn = () => {
    goto(ClinicRoute.Login);
  };
  const gotoSignUp = () => {
    goto(ClinicRoute.Signup);
  };
  const gotoForgotPassword = () => {
    goto(ClinicRoute.ForgotPassword);
  };
  const gotoClinic = (clinicId: string) => {
    goto(clinicId);
  };
  const gotoHome = () => {
    setDemoMode(false);
    goto("");
  };

  const onSignupComplete = async () => {
    setValidUser(true);
    if (demoMode) {
      goto(ClinicRoute.Demo);
    } else gotoHome();
  };

  if (loading) return null;

  return (
    <LandingPageLayout onHome={gotoHome}>
      <Switch>
        <Route
          path={[`*/${ClinicRoute.Demo}`, `*/${ClinicRoute.Demo}/:clinicId`]}
          exact
          render={() => {
            setDemoMode(true);
            return !validUser ? (
              <Redirect to={ClinicRoute.Signup} />
            ) : (
              <DemoCase />
            );
          }}
        />

        <Route
          path={`*/${ClinicRoute.Start}`}
          render={() => <ClinicStart gotoSignUp={gotoSignUp} />}
        />
        <Route
          path={`*/${ClinicRoute.Signup}`}
          render={() => (
            <ClinicSignup
              goToSignIn={gotoSignIn}
              onSignUpComplete={onSignupComplete}
            />
          )}
        />

        <Route
          path={`*/${ClinicRoute.Verify}`}
          render={() => <ClinicVerifySignUp onComplete={onSignupComplete} />}
        />
        <Route
          path={`*/${ClinicRoute.Login}`}
          render={() => (
            <ClinicLogin
              gotoSignUp={gotoSignUp}
              gotoForgotPassword={gotoForgotPassword}
              onLogin={demoMode ? onSignupComplete : gotoClinic}
            />
          )}
        />
        <Route
          path={`*/${ClinicRoute.ForgotPassword}`}
          render={() => <ClinicForgotPassword gotoSignIn={gotoSignIn} />}
        />

        <Route
          path={`*/${ClinicRoute.New}`}
          render={() =>
            !validUser ? (
              <Redirect to={`${ClinicRoute.Login}`} />
            ) : (
              <ClinicCreate />
            )
          }
        />

        <Route
          path={`*/${ClinicRoute.Duplicate}/:clinicId`}
          exact
          render={() =>
            !validUser ? (
              <Redirect to={`${ClinicRoute.Login}`} />
            ) : (
              <ClinicCreate />
            )
          }
        />

        <Route
          path={[
            `*/${ClinicRoute.Welcome}`,
            `*/${ClinicRoute.WelcomeToClinic}`,
          ]}
          render={() => (
            <ClinicWelcome
              gotoSignUp={gotoSignUp}
              gotoSignIn={gotoSignIn}
              clinicName={clinicDisplayName}
            />
          )}
        />

        <Route
          path={"/"}
          render={() =>
            props.invalidClinic ? (
              <ClinicNoAccess
                resetClinic={() => {
                  gotoClinic("");
                }}
              />
            ) : (
              <Redirect
                exact
                to={`${CLINIC_MANAGE_URL}/${ClinicRoute.Login}`}
              />
            )
          }
        />
      </Switch>
    </LandingPageLayout>
  );
};
